import 'wicg-inert'; // Polyfill for inert attribute support in Safari and older browsers
import './utils/announceToScreenReader'; // Pre-initialize the sr-only live region on document.body

import { FC } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { GlobalStyles, GlobalStylesProps } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Highcharts from 'highcharts';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { styled } from '@mui/material/styles';

import { LoadingAnnouncement } from './LoadingAnnouncement';
import { configureAmplify } from './auth/configureAmplify';
import { BasicModalsProvider } from './contexts/BasicModals/BasicModalsProvider';
import { SessionTimeoutProvider } from './contexts/SessionTimeout/SessionTimeoutProvider';
import { queryClient } from './queryClient';
import RouteList from './routes/RouteList';
import { ThemeContextProvider } from './themes/ThemeContextProvider';
import { HIGHLIGHT_COLOR } from './themes/constants';

import '@fontsource/roboto/500.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/700.css';

import { LoadingAnnouncer } from './views/Layout/LoadingAnnouncer/LoadingAnnouncer';

const GLOBAL_STYLES: GlobalStylesProps['styles'] = {
  'html, body, #root': { height: '100%' },
  body: {
    margin: 0,
    padding: 0,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  '#root': { display: 'flex', flexDirection: 'column' },
  code: {
    fontFamily: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
  },
  ':root': { '--tts-highlight-color': HIGHLIGHT_COLOR },
  'mark.tts-hl': {
    backgroundColor: 'var(--tts-highlight-color)',
    textDecoration: 'underline',
    boxDecorationBreak: 'clone',
  },
  'mark.tts-hl *': { backgroundColor: 'inherit !important' },
  '.tts-element-hl': {
    boxShadow: '0 0 4px 8px var(--tts-highlight-color) !important',
    transition: 'box-shadow 120ms ease !important',
  },
};

// Notistack v3 hardcodes inaccessible variant colors (e.g. success #43a047 = 3.3:1 on white).
// styled(MaterialDesignContent) is the only reliable override: MUI's styled() generates a
// scoped emotion class (&.notistack-MuiContent-success) with higher specificity than
// notistack's own makeStyles classes, without needing !important.
const AccessibleSnackbar = styled(MaterialDesignContent)(({ theme }) => ({
  '&.notistack-MuiContent-success': {
    backgroundColor: theme.palette.success.main, // green[800] #2e7d32 — 5.4:1 on white
    color: theme.palette.success.contrastText,
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: theme.palette.error.main, // red[800] #c62828 — 6.1:1 on white
    color: theme.palette.error.contrastText,
  },
  '&.notistack-MuiContent-warning': {
    backgroundColor: theme.palette.warning.main, // orange[900] #e65100 — 4.6:1 on white
    color: theme.palette.warning.contrastText,
  },
  '&.notistack-MuiContent-info': {
    backgroundColor: theme.palette.info.main, // blue[700] #1976d2 — 4.7:1 on white
    color: theme.palette.info.contrastText,
  },
}));

highchartsAccessibility(Highcharts);

configureAmplify();

const App: FC = () => (
  <QueryClientProvider client={queryClient}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <LoadingAnnouncer />
      <ThemeContextProvider>
        <SnackbarProvider
          Components={{
            success: AccessibleSnackbar,
            error: AccessibleSnackbar,
            warning: AccessibleSnackbar,
            info: AccessibleSnackbar,
            loadingAnnouncement: LoadingAnnouncement,
          }}
          domRoot={document.body}
          preventDuplicate
        >
          <GlobalStyles styles={GLOBAL_STYLES} />
          <CssBaseline />
          <BasicModalsProvider>
            <NuqsAdapter
              defaultOptions={{ shallow: true, scroll: true }}
              processUrlSearchParams={(params) => {
                params.sort();
                return params;
              }}
            >
              <BrowserRouter>
                <SessionTimeoutProvider>
                  <RouteList />
                </SessionTimeoutProvider>
              </BrowserRouter>
            </NuqsAdapter>
          </BasicModalsProvider>
        </SnackbarProvider>
      </ThemeContextProvider>
    </LocalizationProvider>

    <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
  </QueryClientProvider>
);

export default App;
